import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment, noSpacesValidator } from '@perval/shared';
import { EmailService } from '../../../../authentification/services/email/email.service';
import {
  CognitoService,
  DrupalService,
  PasswordValidationResults,
  UserLight,
  Utilisateur,
  UtilisateurService,
  passwordValidation,
} from '../../../../shared';
import { ErrorsService } from '../../../../shared/errors/errors.service';
import { BehaviorService } from '../../../../shared/services/behavior/behavior.service';
import { LoginValidators } from '../../../../shared/validators/login-validator';

@Component({
  selector: 'form-validation-collab',
  templateUrl: './form-validation.component.html',
  styleUrls: ['./form-validation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormValidationComponent implements OnInit, OnChanges {
  @Input() utilisateurToValidate!: UserLight;
  @Input() crpcen!: string;
  @Input() codeValidation!: string;

  user!: Utilisateur;
  loading!: boolean;
  showErrorMsg = false;
  errorMsg = '';
  showFormCollabContent = true;
  showSuccessComp = false;
  showFailComp = false;
  isPasswordDisplayed = false;
  passwordInputType: 'text' | 'password' = 'password';
  isConfirmPasswordDisplayed = false;
  confirmPasswordInputType: 'text' | 'password' = 'password';
  cbType = UserLight.Type.CB;

  styleValidatorNumber: string = 'text-danger';
  styleValidatorUppercase: string = 'text-danger';
  styleValidatorLowercase: string = 'text-danger';
  styleValidatorSpecial: string = 'text-danger';
  styleValidatorSize: string = 'text-danger';

  validationForm = new FormGroup({
    login: new FormControl('', [
      Validators.required,
      noSpacesValidator(),
      LoginValidators.formatValidator(),
      LoginValidators.maxLengthValidator(),
    ]),
    password1: new FormControl('', [Validators.required]),
    password2: new FormControl('', [Validators.required]),
  });

  constructor(
    private readonly utilisateurService: UtilisateurService,
    private readonly cognitoService: CognitoService,
    private readonly drupalService: DrupalService,
    private readonly emailService: EmailService,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly errorsService: ErrorsService,
    private readonly behaviorService: BehaviorService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const currenteValue = changes['utilisateurToValidate']
      ?.currentValue as UserLight;

    if (currenteValue) {
      if (currenteValue.login) {
        this.validationForm.controls.login.setValue(currenteValue.login);
      }
      this.user = {
        ...this.user,
        email: currenteValue.email,
        nom: currenteValue.lastName,
        prenom: currenteValue.firstName,
        idOrganisme: currenteValue.organizationCode,
        codeValidation: this.codeValidation,
      };
    }
  }

  ngOnInit() {
    this.loading = false;
  }

  public passwordChanges() {
    if (this.validationForm.value.password1) {
      const redAlert: string = 'text-danger';
      const greenOk: string = 'text-success';

      const regNumber = new RegExp(/([0-9])/);
      if (regNumber.test(this.validationForm.value.password1)) {
        this.styleValidatorNumber = greenOk;
      } else {
        this.styleValidatorNumber = redAlert;
      }

      const regUppercase = new RegExp(/([A-Z])/);
      if (regUppercase.test(this.validationForm.value.password1)) {
        this.styleValidatorUppercase = greenOk;
      } else {
        this.styleValidatorUppercase = redAlert;
      }

      const reglowercase = new RegExp(/([a-z])/);
      if (reglowercase.test(this.validationForm.value.password1)) {
        this.styleValidatorLowercase = greenOk;
      } else {
        this.styleValidatorLowercase = redAlert;
      }

      const regSpecial = new RegExp(/[#?!@$%^&*-.]/);
      if (regSpecial.test(this.validationForm.value.password1)) {
        this.styleValidatorSpecial = greenOk;
      } else {
        this.styleValidatorSpecial = redAlert;
      }

      const regSize = new RegExp(/.{12,}$/);
      if (regSize.test(this.validationForm.value.password1)) {
        this.styleValidatorSize = greenOk;
      } else {
        this.styleValidatorSize = redAlert;
      }
    }
  }

  // TODO : refactor this BIG function
  public async onValidateCollab(): Promise<void> {
    this.loading = true;
    this.showErrorMsg = false;
    this.errorMsg = '';

    const passwordValidationResults = passwordValidation(
      this.validationForm.value.password1!,
      this.validationForm.value.password2!,
    );
    switch (passwordValidationResults) {
      case PasswordValidationResults.BOTH_DIFFERENTS:
        this.showErrorMsg = true;
        this.errorMsg = 'Les 2 mots de passe doivent être identiques';
        this.behaviorService.scrollToTheTop();
        break;
      case PasswordValidationResults.NOT_MATCHING:
        this.showErrorMsg = true;
        this.errorMsg =
          'Mot de passe incorrect : au moins un chiffre, un caractère spécial, une minuscule, une majuscule et minimum 12 caractères';
        this.behaviorService.scrollToTheTop();
        break;
      case PasswordValidationResults.OK:
        this.user = {
          ...this.user,
          login: (this.validationForm.value.login as string).toLowerCase(),
        };

        // eslint-disable-next-line no-case-declarations
        let succes = true;

        try {
          if (
            this.validationForm.value.password1 &&
            this.validationForm.value.password2
          ) {
            await this.utilisateurService.validateUtilisateur(
              this.user,
              this.validationForm.value.password1,
              this.validationForm.value.password2,
              this.utilisateurToValidate.type ?? UserLight.Type.AUTRE,
            );
          }
        } catch (err: any) {
          this.loading = false;
          this.showErrorMsg = true;
          this.errorMsg = err;
          this.behaviorService.scrollToTheTop();
          succes = false;
        }

        if (succes) {
          try {
            await this.cognitoService.signIn(
              this.user.login,
              <string>this.validationForm.value.password1,
            );

            await this.drupalService.createSession();

            this.user = await this.utilisateurService.getUtilisateurByLogin(
              this.user.login,
            );

            await this.emailService.sendEmailConfirmationUtilisateur(this.user);
            const gestionnaire: Utilisateur | undefined =
              this.user.type !== UserLight.Type.CB
                ? await this.utilisateurService.getUtilisateurById(
                    this.user.gestionnaireId!,
                  )
                : undefined;
            if (
              gestionnaire &&
              gestionnaire.email !== null &&
              gestionnaire.email !== ''
            ) {
              await this.emailService.sendEmailConfirmationGestionnaire(
                this.user,
                gestionnaire,
              );
            }
            if (this.utilisateurToValidate.type === UserLight.Type.CB) {
              this.router.navigate(['/commande-de-credits']);
              return;
            }
            window.location.href = `${environment.host}/content/accueil`;
          } catch {
            this.errorMsg = 'Erreur inconnue';
            this.behaviorService.scrollToTheTop();
            this.showErrorMsg = true;
          }
        }

        break;
      default:
        this.errorMsg = this.errorsService.getError('DEFAULT');
        this.showErrorMsg = true;
        this.behaviorService.scrollToTheTop();
    }
    this.loading = false;
    this.changeDetector.markForCheck();
  }

  public showPassword(): void {
    this.isPasswordDisplayed = !this.isPasswordDisplayed;
    this.passwordInputType = this.isPasswordDisplayed ? 'text' : 'password';
  }

  public showConfirmPassword(): void {
    this.isConfirmPasswordDisplayed = !this.isConfirmPasswordDisplayed;
    this.confirmPasswordInputType = this.isConfirmPasswordDisplayed
      ? 'text'
      : 'password';
  }
}
